// A form control that picks ONE employee by name or sicil (employee number).
//
// Admin forms used to ask for the raw `employee_id` in a text box. A person can
// not know a system UUID, and typing the sicil they DO know ("E-1003") failed
// with a validation error that named no field. This searches on the server, so
// it reaches anyone in a tenant past the first page (the same reason
// roles-permissions moved to `searchEmployees`, SEM-8105), and shows the sicil in
// every option so two people with one name can be told apart.
//
// It is a search box over a <select>, not a custom listbox: that is the
// pattern the product already uses for person pickers, it keeps the native
// keyboard and screen-reader behaviour, and it needs no new shared component.
//
// The chosen id travels in a hidden input named `name`, so an <EntityForm>'s
// FormData collects it like any other field. Every string comes from the host
// (already localized): this file owns no copy.

import { useEffect, useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { SearchInput, Select } from "@novemai/components";

import { searchEmployees, type EmployeeRow } from "./api";

export interface EmployeeSearchPickerLabels {
  readonly label: string;
  readonly searchLabel: string;
  readonly searchPlaceholder: string;
  /** The empty option, e.g. "Choose an employee". */
  readonly choose: string;
  readonly noMatches: string;
  /** Shown when the server has more matches than one page returned. */
  readonly truncated: string;
}

export interface EmployeeSearchPickerProps {
  /** FormData key the chosen employee_id is submitted under. */
  readonly name: string;
  readonly labels: EmployeeSearchPickerLabels;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  /**
   * Called with the row a person picks. For hosts that ACT on a pick (append it
   * to a list) instead of submitting a form: they need the whole row, not just
   * the id in the hidden input. Remount the picker (a changed `key`) to clear it
   * after use.
   */
  readonly onSelect?: (employee: EmployeeRow) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/** Debounce inside one instance: a page-level URL debounce would make every
 *  picker on a screen share, and overwrite, one param. */
function useDebouncedValue(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === debounced) return;
    const timer = window.setTimeout(() => setDebounced(value), SEARCH_DEBOUNCE_MS);
    return (): void => window.clearTimeout(timer);
  }, [debounced, value]);
  return debounced;
}

function optionLabel(row: EmployeeRow): string {
  const name = `${row.legal_first_name} ${row.legal_last_name}`;
  return row.employee_number === null || row.employee_number === ""
    ? name
    : `${name} · ${row.employee_number}`;
}

export function EmployeeSearchPicker({
  name,
  labels,
  required = false,
  disabled = false,
  error,
  onSelect,
}: EmployeeSearchPickerProps): React.ReactElement {
  const id = useId();
  const selectId = `${id}-select`;
  const errorId = `${id}-error`;
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search);
  // Kept as a row, not just an id: a later search can drop the chosen person
  // from the matches, and a <select> whose value matches no option renders blank
  // while still submitting that value.
  const [selected, setSelected] = useState<EmployeeRow | null>(null);

  const matches = useQuery({
    queryKey: ["employees", "picker", debounced],
    queryFn: () => searchEmployees(debounced),
    // A picker's option list is throwaway; keeping the previous matches on
    // screen while the next ones load stops the list flashing empty per letter.
    placeholderData: (previous) => previous,
  });

  const rows = matches.data?.items ?? [];
  const options =
    selected !== null && !rows.some((row) => row.employee_id === selected.employee_id)
      ? [selected, ...rows]
      : rows;

  return (
    <div data-slot="employee-search-picker" className="nai-form-field">
      <div className="nai-form-field-labelrow">
        <label htmlFor={selectId} className="nai-form-field-label">
          {labels.label}
          {required && (
            <span className="nai-form-field-required" aria-hidden="true">
              {" "}
              ·
            </span>
          )}
        </label>
      </div>
      <input type="hidden" name={name} value={selected?.employee_id ?? ""} />
      <SearchInput
        aria-label={labels.searchLabel}
        placeholder={labels.searchPlaceholder}
        value={search}
        disabled={disabled}
        onChange={(event): void => setSearch(event.target.value)}
        // Inside a <form>, Enter in a search box submits it. Here Enter only
        // means "I am done typing", and submitting would raise a required-field
        // error the person has not had a chance to answer yet.
        onKeyDown={(event): void => {
          if (event.key === "Enter") event.preventDefault();
        }}
      />
      <Select
        id={selectId}
        value={selected?.employee_id ?? ""}
        disabled={disabled}
        error={error !== undefined}
        aria-required={required || undefined}
        aria-describedby={error !== undefined ? errorId : undefined}
        onChange={(event): void => {
          const picked = options.find((row) => row.employee_id === event.target.value) ?? null;
          setSelected(picked);
          if (picked !== null) onSelect?.(picked);
        }}
      >
        <option value="">{labels.choose}</option>
        {options.map((row) => (
          <option key={row.employee_id} value={row.employee_id}>
            {optionLabel(row)}
          </option>
        ))}
      </Select>
      {matches.isSuccess && rows.length === 0 && (
        <p className="nai-form-field-hint">{labels.noMatches}</p>
      )}
      {matches.data?.nextCursor != null && (
        <p className="nai-form-field-hint">{labels.truncated}</p>
      )}
      {error !== undefined && (
        <p id={errorId} role="alert" className="nai-form-field-error">
          {error}
        </p>
      )}
    </div>
  );
}
